import type { FunctionComponent } from "react";
import { useMemo, useState } from "react";

import { config } from "shared/data";

import { usePricing, useTracking } from "./hooks";
import type { Props as PlanOptionProps } from "./plan-option";
import { Container, EPaperPlan, Footer, PlanContainer, PrintPlan, Title, Toggle } from "./styles";
import type { BillingCycle, Plan, Resource, Variant } from "./types";

type Props = {
  className?: string;
  variant: Variant;
};
export const SubscriptionWidget: FunctionComponent<Props> = ({ className, variant }) => {
  const { trackBillingCycleToggle, trackPlanSelected } = useTracking();

  const [selectedBillingCycle, setSelectedBillingCycle] = useState<BillingCycle>("1y");
  const [selectedResource, setSelectedResource] = useState<Resource>("postiesPrintAndDigital");
  const { pricing } = usePricing();
  const { plans, selectedPlan } = useMemo(() => {
    if (!pricing) return {};
    const resources: Resource[] = ["postiesEPaperAndDigital", "postiesPrintAndDigital"];
    const plans = resources.reduce(
      (plans, resource) => {
        plans[resource] = {
          billingCycle: selectedBillingCycle,
          pricing: pricing[resource][selectedBillingCycle],
          resource,
        };
        return plans;
      },
      {} as Record<Resource, Plan>,
    );

    return { plans, selectedPlan: plans[selectedResource] };
  }, [selectedBillingCycle, selectedResource, pricing]);
  if (!plans) return null;

  const computeViewModelVariant = () => {
    switch (variant) {
      case "brochure":
        return {
          handleResourceSelected: setSelectedResource,
          hideSubscribeButton: false,
          withBalloon: true,
        };
      case "paywall":
        return {
          hideSubscribeButton: true,
          withBalloon: false,
          withLink: true,
        };
    }
  };
  const viewModelVariant = computeViewModelVariant();

  const computeSubscribeLink = (plan: Plan) => {
    // cspell: ignore postiesbundle
    const { billingCycle, resource } = plan;
    const subscribeHost = config.rosetta.subscribeHost;
    const resourceName = resource === "postiesEPaperAndDigital" ? "posties" : "postiesbundle";
    return `${subscribeHost}/posties?plan=${resourceName}${billingCycle}`;
  };
  const computePlanOptionProps = (plan: Plan) => {
    const props: PlanOptionProps = {
      handleSelected() {
        trackPlanSelected(plan);
        viewModelVariant.handleResourceSelected?.(plan.resource);
      },
      isSelected: plan === selectedPlan,
      plan,
      withBalloon: viewModelVariant.withBalloon,
      withLink: viewModelVariant.withLink
        ? {
            url: computeSubscribeLink(plan),
          }
        : undefined,
    };
    return props;
  };

  return (
    <Container className={className}>
      <Title>Pick a plan that suits you</Title>
      <Toggle
        billingCycle={selectedBillingCycle}
        handleChange={value => {
          trackBillingCycleToggle(value);
          setSelectedBillingCycle(value);
          value === "1m" && setSelectedResource("postiesEPaperAndDigital");
        }}
      />
      <PlanContainer>
        <EPaperPlan {...computePlanOptionProps(plans.postiesEPaperAndDigital)} />
        <PrintPlan {...computePlanOptionProps(plans.postiesPrintAndDigital)} />
      </PlanContainer>
      <Footer
        disableSubscribeButton={selectedPlan.pricing.isDisabled}
        hideSubscribeButton={viewModelVariant.hideSubscribeButton}
        subscribeUrl={computeSubscribeLink(selectedPlan)}
        variant={variant}
      />
    </Container>
  );
};

SubscriptionWidget.displayName = "SubscriptionWidget";
